import { inject, Injectable } from '@angular/core';
import { LimitedCurrencyAmount } from '@lx/currency/shared/model/currency.model';
import { formatDate, YesNo } from '@lx/quote-tool/shared/google-analytics';
import { Observable } from 'rxjs';
import { combineLatestWith, map } from 'rxjs/operators';
import { OnlineQuoteToolStore, QuoteStore } from '../store';
import { GoogleAnalyticsOnlineQuoteToolQuote } from './google-analytics-parameters.type';
import { ClassTimeTimezoneDefaultsService } from '../services';

/**
 * Service to provide quote-related data for Google Analytics dataLayer object.
 * Simpler than QT Lite since Online QT doesn't have extras like insurance, transfers, etc.
 */
@Injectable({ providedIn: 'root' })
export class QuoteDataLayerService {
  private readonly onlineQuoteToolStore = inject(OnlineQuoteToolStore);
  private readonly quoteStore = inject(QuoteStore);
  private readonly classTimeTimezoneDefaults = inject(ClassTimeTimezoneDefaultsService);

  /**
   * Gets the base quote data for Google Analytics dataLayer object.
   * Includes: quote number, program type, course, duration, dates, class time, timezone.
   * Class time and timezone are always obtained from ClassTimeTimezoneDefaultsService.
   *
   * @returns Observable of base quote data
   */
  getBaseData(): Observable<Partial<GoogleAnalyticsOnlineQuoteToolQuote>> {
    return this.onlineQuoteToolStore.baseSelectionForDataLayer$.pipe(
      combineLatestWith(this.classTimeTimezoneDefaults.getBrowserTimezoneName$()),
      map(([{ quote, programType, course, duration, startDate, endDate }, timezoneName]) => {
        const classTimeLabel = this.classTimeTimezoneDefaults.getDefaultClassTimeLabel();

        return this.mapToDataLayerQuote({
          quote,
          programType,
          course,
          duration,
          startDate,
          endDate,
          classTimeLabel,
          timezoneName,
        });
      }),
    );
  }

  /**
   * Maps store selection data to Google Analytics dataLayer quote format.
   * @private
   */
  private mapToDataLayerQuote(data: {
    quote: number;
    programType: string;
    course: string;
    duration: number;
    startDate: string;
    endDate: string;
    classTimeLabel: string;
    timezoneName: string | undefined;
  }): Partial<GoogleAnalyticsOnlineQuoteToolQuote> {
    return {
      quote: data.quote,
      quote_program_type: data.programType,
      quote_course_detail: data.course,
      quote_duration_interest: data.duration,
      quote_start_date: formatDate(data.startDate),
      quote_end_date: formatDate(data.endDate),
      quote_class_time: data.classTimeLabel,
      quote_time_zone: data.timezoneName,
    };
  }

  /**
   * Gets the quote prices related data for Google Analytics dataLayer object.
   * Includes: promo applied, prices, currencies, discount amount.
   *
   * @returns Observable of prices data
   */
  getPricesData(): Observable<Partial<GoogleAnalyticsOnlineQuoteToolQuote>> {
    return this.quoteStore.pricesSelectionForDataLayer$.pipe(
      map(({ productTotal, grandTotal, destinationCurrency, displayCurrency, discountTotalAsPercentage, discountTotal }) => ({
        quote_promo_applied: discountTotalAsPercentage ? YesNo.Yes : YesNo.No,
        quote_price_discounted: this.getPriceInCurrency(grandTotal, displayCurrency),
        quote_local_currency: displayCurrency,
        quote_destination_currency: destinationCurrency,
        quote_total_amount: this.getPriceInCurrency(grandTotal, displayCurrency),
        quote_price_original: this.getPriceInCurrency(productTotal, displayCurrency),
        quote_discount_amount: this.getPriceInCurrency(discountTotal, displayCurrency),
      })),
    );
  }

  /**
   * Extracts price value in the specified currency from an array of price amounts.
   *
   * @param price - Array of price amounts in different currencies
   * @param currency - Currency code to extract
   * @returns Price value as string, rounded to nearest integer, or undefined if not found
   * @private
   */
  private getPriceInCurrency(price: LimitedCurrencyAmount[], currency: string | undefined): string | undefined {
    if (!currency || !price?.length) {
      return undefined;
    }

    const priceInCurrency = price.find((p) => p.currency === currency);
    return priceInCurrency ? Math.round(priceInCurrency.value).toString() : undefined;
  }
}
